<template>
  <dialog
    ref="previewDialog"
    class="certificate-preview"
    aria-labelledby="certificate-preview-title"
    @click="handleDialogBackdrop"
    @close="handleDialogClose"
  >
    <div v-if="certificate" class="certificate-preview-shell">
      <button
        class="certificate-preview-close"
        type="button"
        aria-label="关闭证书预览"
        @click="closePreview"
      >
        <i class="i-lucide-x" aria-hidden="true"></i>
      </button>

      <div class="certificate-preview-media">
        <certificate-media
          :source="certificate.cover_url"
          :alt="certificate.image_alt || certificate.name"
          loading="eager"
        />
      </div>

      <div class="certificate-preview-copy">
        <span class="certificate-preview-category">{{ certificate.category.name }}</span>
        <h2 id="certificate-preview-title">{{ certificate.name }}</h2>

        <dl v-if="hasCertificateDetails(certificate)" class="certificate-preview-meta">
          <div v-if="certificate.issuer">
            <dt>颁发机构</dt>
            <dd>{{ certificate.issuer }}</dd>
          </div>
          <div v-if="certificate.granted_date">
            <dt>颁发日期</dt>
            <dd>{{ formatDate(certificate.granted_date) }}</dd>
          </div>
          <div v-if="certificate.level">
            <dt>证书级别</dt>
            <dd>{{ certificate.level }}</dd>
          </div>
          <div v-if="validityText(certificate)">
            <dt>有效期限</dt>
            <dd>{{ validityText(certificate) }}</dd>
          </div>
        </dl>

        <p v-if="certificate.scope" class="certificate-preview-scope">
          {{ certificate.scope }}
        </p>

        <a
          v-if="certificate.verify_url"
          class="certificate-verify-link"
          :href="certificate.verify_url"
          target="_blank"
          rel="noopener noreferrer"
        >
          在线核验
          <i class="i-lucide-external-link" aria-hidden="true"></i>
        </a>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
  import type { CertificateItem } from '~/types/site-api'

  interface Props {
    certificate?: CertificateItem
  }

  defineOptions({
    name: 'CertificatePreview',
  })

  const props = defineProps<Props>()
  const emit = defineEmits<{
    close: []
  }>()

  const previewDialog = useTemplateRef<HTMLDialogElement>('previewDialog')
  const certificate = computed(() => props.certificate)

  /** 关闭证书预览。 */
  function closePreview() {
    previewDialog.value?.close()
  }

  /** 点击遮罩区域时关闭预览。 */
  function handleDialogBackdrop(event: MouseEvent) {
    if (event.target === previewDialog.value) closePreview()
  }

  /** Dialog 关闭后通知画廊清理当前证书。 */
  function handleDialogClose() {
    emit('close')
  }

  /** 判断当前证书是否存在可展示的结构化信息。 */
  function hasCertificateDetails(item: CertificateItem) {
    return Boolean(item.issuer || item.granted_date || item.level || validityText(item))
  }

  /** 统一日期展示格式。 */
  function formatDate(date: string) {
    return date.replaceAll('-', '.')
  }

  /** 根据接口有效期字段生成中文展示文本。 */
  function validityText(item: CertificateItem) {
    if (item.expire_date) {
      const start = item.valid_from || item.granted_date
      return start
        ? `${formatDate(start)} - ${formatDate(item.expire_date)}`
        : formatDate(item.expire_date)
    }
    if (item.validity_status === 'permanent') return '长期有效'
    if (item.validity_status === 'not_applicable') return '不适用'
    if (item.validity_status === 'unknown') return '待确认'
    return ''
  }

  watch(certificate, async (item) => {
    if (!item) {
      if (previewDialog.value?.open) previewDialog.value.close()
      return
    }

    await nextTick()
    if (previewDialog.value && !previewDialog.value.open) previewDialog.value.showModal()
  })
</script>

<style scoped lang="scss">
  .certificate-preview {
    position: fixed;
    inset: 0;
    width: min(1100px, calc(100vw - 48px));
    max-width: none;
    max-height: calc(100vh - 48px);
    padding: 0;
    margin: auto;
    overflow: hidden;
    color: var(--ink);
    background: var(--surface);
    border: 0;
    border-radius: var(--radius-md);
    box-shadow: 0 28px 80px rgb(26 10 12 / 32%);
  }

  .certificate-preview::backdrop {
    background: rgb(24 12 14 / 78%);
    backdrop-filter: blur(5px);
  }

  .certificate-preview-shell {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.65fr);
    height: min(680px, calc(100vh - 48px));
    overflow: hidden;
    border-radius: inherit;
  }

  .certificate-preview-close {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    color: var(--ink);
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    transition: color 180ms ease;
  }

  .certificate-preview-close:hover {
    color: var(--accent);
  }

  .certificate-preview-close i {
    width: 19px;
    height: 19px;
  }

  .certificate-preview-media {
    min-width: 0;
    min-height: 0;
    padding: 34px;
    background: #eceeef;
  }

  .certificate-preview-media :deep(.certificate-media) {
    background: transparent;
  }

  .certificate-preview-copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    padding: 56px 44px;
    overflow-y: auto;
  }

  .certificate-preview-category {
    font-size: 12px;
    font-weight: 650;
    color: var(--accent);
  }

  .certificate-preview-copy h2 {
    margin: 12px 0 0;
    font-size: 32px;
    line-height: 1.25;
  }

  .certificate-preview-meta {
    display: grid;
    gap: 13px;
    margin: 30px 0 0;
  }

  .certificate-preview-meta > div {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 16px;
    padding-bottom: 13px;
    border-bottom: 1px solid var(--line);
  }

  .certificate-preview-meta dt,
  .certificate-preview-meta dd {
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
  }

  .certificate-preview-meta dt {
    color: var(--muted);
  }

  .certificate-preview-meta dd {
    color: var(--ink);
  }

  .certificate-preview-scope {
    margin: 22px 0 0;
    font-size: 13px;
    line-height: 1.8;
    color: var(--muted);
  }

  .certificate-verify-link {
    display: inline-flex;
    gap: 7px;
    align-items: center;
    align-self: flex-start;
    margin-top: 24px;
    font-size: 13px;
    color: var(--accent);
    cursor: pointer;
  }

  .certificate-verify-link i {
    width: 15px;
    height: 15px;
  }

  @media (max-width: 1023px) {
    .certificate-preview-shell {
      grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
    }
  }

  @media (max-width: 767px) {
    .certificate-preview {
      width: calc(100vw - 24px);
      max-height: calc(100vh - 24px);
    }

    .certificate-preview-shell {
      display: flex;
      flex-direction: column;
      height: auto;
      min-height: 0;
      max-height: calc(100vh - 24px);
      overflow-y: auto;
    }

    .certificate-preview-media {
      flex: 0 0 min(54vh, 460px);
      min-height: 300px;
      padding: 20px;
    }

    .certificate-preview-copy {
      flex: 0 0 auto;
      padding: 28px 22px 32px;
      overflow: visible;
    }

    .certificate-preview-copy h2 {
      padding-right: 38px;
      font-size: 24px;
    }
  }
</style>
